/*
  KP: behaviour for the small animations and smoothness fixes in snippets/kp-motion.liquid.
  The numbers match the numbered blocks in that file.

  1. Product photo glide: when a product tile is pressed, its photo is given the same name as
     the product page's main photo, so the browser glides one into the other.
  2. Press feedback: tells iPhones to show "pressed" styles, and makes product tiles and
     accessory rows dip when tapped (held long enough to be seen on a quick tap, and not
     shown when the finger is only scrolling). A tile that is opening its product stays
     dipped until the product page takes over, so there is something to see while waiting.
  2b. The whole product tile opens the product, wherever on it the tap lands (not only the
     photo and the name). Without this a tap on the price or the space around them made
     the tile dip and then did nothing, which looked like a tap that was not noticed.
  3. Add to cart: a tick on the button once the item is in the cart.
  5. Model change on collection pages: the old tile photo fades out over the new one.
  6. No jumping content: closes the space kept for the "Add accessories" panel on product
     pages where no panel turns up.
  7. Phones: the "Add to Cart" bar on product pages. Its button shows the same "working"
     line and the same tick as the main Add to Cart button, at the same moments.
*/
(function () {
  'use strict';

  var reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var isProductPage = document.body.classList.contains('template-product');

  // iPhones only apply "pressed" (:active) styles when the page listens for touches.
  document.addEventListener('touchstart', function () {}, { passive: true });

  // The tile whose product is being opened: it keeps its dip until the next page shows
  // (or for four seconds at most, in case the page never comes).
  var leavingTile = null;

  function holdDip(tile) {
    leavingTile = tile;
    tile.classList.add('kp-pressed');
    setTimeout(function () {
      if (leavingTile !== tile) return;
      leavingTile = null;
      tile.classList.remove('kp-pressed');
    }, 4000);
  }

  // A plain press that will open the link in this same tab (not "open in new tab").
  function opensHere(event, link) {
    return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!link || link.target !== '_blank');
  }

  /* ---- 1. Product photo glide ---- */

  var namedPhoto = null;

  function clearPhotoName() {
    if (namedPhoto) namedPhoto.style.viewTransitionName = '';
    namedPhoto = null;
    document.documentElement.classList.remove('kp-photo-from-tile');
  }

  if (!reducedMotion) {
    document.addEventListener(
      'click',
      function (event) {
        var link = event.target && event.target.closest ? event.target.closest('a[href*="/products/"]') : null;
        var tile = link ? link.closest('.product-item') : null;
        var photo = tile ? tile.querySelector('.product-item__image') : null;
        if (!photo) return;

        clearPhotoName();
        // Only one thing on a page may carry the name: see the style in kp-motion.liquid.
        document.documentElement.classList.add('kp-photo-from-tile');
        photo.style.viewTransitionName = 'kp-product-media';
        namedPhoto = photo;

        // Block 2: stay dipped while the product page is on its way.
        if (opensHere(event, link)) holdDip(tile);
      },
      true
    );

    // Coming back to this page with the browser's Back button: start clean.
    window.addEventListener('pageshow', clearPhotoName);
  }

  /* ---- 2. Press feedback for product tiles and accessory rows ---- */

  // A quick tap is over before a "pressed" style can be seen, and a finger that is only
  // scrolling should not make things dip. So: wait a moment before dipping (a scroll cancels
  // it), and once dipped, stay dipped for a short minimum time.
  if (!reducedMotion && window.PointerEvent) {
    var PRESSABLE = '.product-item, .kp-addons__item';
    var SKIP = 'button, select, .product-item__quick-add';
    var pressed = null;
    var pressTimer = null;

    var release = function (element, delay) {
      setTimeout(function () {
        // A tile that is opening its product keeps its dip (see holdDip).
        if (element !== leavingTile) element.classList.remove('kp-pressed');
      }, delay);
    };

    document.addEventListener(
      'pointerdown',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var element = target.closest(PRESSABLE);
        if (!element || target.closest(SKIP)) return;

        pressed = element;
        clearTimeout(pressTimer);
        pressTimer = setTimeout(function () {
          if (pressed === element) element.classList.add('kp-pressed');
        }, 70);
      },
      { passive: true }
    );

    document.addEventListener(
      'pointerup',
      function () {
        if (!pressed) return;
        var element = pressed;
        pressed = null;
        clearTimeout(pressTimer);
        // A tap shorter than the wait above still gets its dip.
        element.classList.add('kp-pressed');
        release(element, 170);
      },
      { passive: true }
    );

    // The finger started scrolling (or the press was abandoned): no dip.
    document.addEventListener(
      'pointercancel',
      function () {
        if (!pressed) return;
        clearTimeout(pressTimer);
        pressed.classList.remove('kp-pressed');
        pressed = null;
      },
      { passive: true }
    );

    // Back button: do not come back to a tile that is still dipped.
    window.addEventListener('pageshow', function () {
      leavingTile = null;
      var stuck = document.querySelectorAll('.kp-pressed');
      for (var i = 0; i < stuck.length; i++) stuck[i].classList.remove('kp-pressed');
    });
  }

  /* ---- 2b. The whole product tile opens the product ---- */

  // Things inside a tile that do their own job when pressed, and must be left to it.
  var OWN_JOB = 'a, button, select, input, textarea, label, summary, [role="button"], .product-item__quick-add, .product-item__swatches';
  var PRODUCT_LINK = 'a[href*="/products/"]';

  // Where the press began. A tile dips (shrinks a little) while it is pressed, so a press
  // right on its edge can end just outside it; remembering where it began lets that press
  // count as well.
  var down = null;

  if (window.PointerEvent) {
    document.addEventListener(
      'pointerdown',
      function (event) {
        var target = event.target;
        var tile = target && target.closest ? target.closest('.product-item') : null;

        down = tile
          ? {
              tile: tile,
              x: event.clientX,
              y: event.clientY,
              at: Date.now(),
              // Meant to open the product: on the product's own link, or on nothing with a job of its own.
              opens: !!target.closest(PRODUCT_LINK) || !target.closest(OWN_JOB)
            }
          : null;
      },
      { passive: true }
    );
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (event.defaultPrevented || !target || !target.closest || !opensHere(event, null)) return;

    // A real link or button was pressed: it is already doing its job.
    if (target.closest(OWN_JOB)) return;

    var tile = target.closest('.product-item');

    // Ended just outside a tile that the press began on (see "down" above).
    if (!tile && down && down.opens && Date.now() - down.at < 1200 && Math.abs(event.clientX - down.x) < 12 && Math.abs(event.clientY - down.y) < 12) {
      tile = down.tile;
    }

    if (!tile) return;

    // Dragging to select some text is not a press.
    if (window.getSelection && String(window.getSelection())) return;

    var link = tile.querySelector(PRODUCT_LINK);
    // Pressing the link ourselves runs everything a real press on it would (block 1 too).
    if (link) link.click();
  });

  /* ---- 3. Add to cart tick ---- */

  var pressedButton = null;
  var pressedAt = 0;

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target && event.target.closest ? event.target.closest('[data-add-to-cart]') : null;
      if (button) {
        pressedButton = button;
        pressedAt = Date.now();
      }
    },
    true
  );

  function showTick() {
    var button = pressedButton;
    if (!button || Date.now() - pressedAt > 10000) return;
    pressedButton = null;

    button.classList.add('kp-added');
    setTimeout(function () {
      button.classList.remove('kp-added');
    }, 1600);
  }

  // The browser reports when a request to /cart/add has finished, whoever made it.
  if (window.PerformanceObserver) {
    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        for (var i = 0; i < entries.length; i++) {
          if (!/\/cart\/add/.test(entries[i].name)) continue;
          // Not added (sold out, for example): no tick. Older browsers do not say; assume it worked.
          if (entries[i].responseStatus && entries[i].responseStatus >= 400) continue;
          showTick();
          return;
        }
      }).observe({ type: 'resource', buffered: false });
    } catch (e) {
      /* No tick on browsers without this; nothing else is affected. */
    }
  }

  /* ---- 5. Model change on collection pages: cross-fade the tile photos ---- */

  // assets/kp-case-filters.js swaps a tile's photo by changing the address on the same
  // picture. Here a copy of the old photo is laid over the tile and faded out once the new
  // one has arrived. Only tiles on or near the screen are done; the rest just swap.
  var main = document.getElementById('main');

  if (main && window.MutationObserver && !reducedMotion && main.querySelector('.product-item')) {
    var shownPhoto = typeof WeakMap === 'function' ? new WeakMap() : null;

    var isTilePhoto = function (node) {
      return !!node && node.tagName === 'IMG' && node.classList.contains('image__img') && !!node.closest('.product-item');
    };

    var notePhoto = function (image) {
      if (shownPhoto && image.currentSrc) shownPhoto.set(image, image.currentSrc);
    };

    var fadeOut = function (cover) {
      if (!cover.parentNode || cover.classList.contains('is-leaving')) return;
      cover.classList.add('is-leaving');
      setTimeout(function () {
        if (cover.parentNode) cover.parentNode.removeChild(cover);
      }, 320);
    };

    if (shownPhoto) {
      var existing = main.querySelectorAll('.product-item img.image__img');
      for (var p = 0; p < existing.length; p++) {
        if (existing[p].complete) notePhoto(existing[p]);
      }

      // "load" does not bubble, so listen on the way down.
      main.addEventListener(
        'load',
        function (event) {
          if (!isTilePhoto(event.target)) return;
          notePhoto(event.target);
          var host = event.target.parentNode;
          var cover = host ? host.querySelector('.kp-tile-fade') : null;
          if (cover) fadeOut(cover);
        },
        true
      );

      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var image = records[i].target;
          if (!isTilePhoto(image)) continue;

          var host = image.parentNode;
          var old = shownPhoto.get(image);
          if (!host || !old || host.querySelector('.kp-tile-fade')) continue;

          var box = image.getBoundingClientRect();
          if (!box.width || box.bottom < -200 || box.top > window.innerHeight * 1.5) continue;

          var cover = document.createElement('img');
          cover.className = 'kp-tile-fade';
          cover.alt = '';
          cover.setAttribute('aria-hidden', 'true');
          cover.style.objectFit = window.getComputedStyle(image).objectFit;
          cover.src = old;

          host.classList.add('kp-tile-fade-host');
          host.appendChild(cover);

          // If the new photo never reports back, do not leave the old one sitting there.
          (function (stuck) {
            setTimeout(function () {
              fadeOut(stuck);
            }, 1500);
          })(cover);
        }
      }).observe(main, { attributes: true, attributeFilter: ['src', 'srcset'], subtree: true });
    }
  }

  /* ---- 6. Close the space kept for the accessories panel when none turns up ---- */

  if (isProductPage) {
    var settleAccessories = function () {
      setTimeout(function () {
        if (!document.querySelector('.kp-addons')) {
          document.documentElement.classList.add('kp-addons-none');
        }
      }, 2500);
    };

    if (document.readyState === 'complete') {
      settleAccessories();
    } else {
      window.addEventListener('load', settleAccessories);
    }
  }

  /* ---- 7. Phones: "Add to Cart" bar on product pages ---- */

  (function stickyBar() {
    if (!isProductPage) return;

    var product = document.querySelector('main .product');
    if (!product) return;

    // The theme throws the Add to Cart button away and makes a new one every time a model or
    // case type is chosen, so it is looked up afresh each time rather than kept hold of.
    function mainButton() {
      return product.querySelector('[data-add-to-cart]');
    }

    if (!mainButton()) return;

    function make(tag, className) {
      var element = document.createElement(tag);
      element.className = className;
      return element;
    }

    var bar = make('div', 'kp-sticky-atc');
    bar.setAttribute('aria-hidden', 'true');

    var photo = make('img', 'kp-sticky-atc__image');
    photo.alt = '';
    photo.hidden = true;

    var info = make('div', 'kp-sticky-atc__info');
    var title = make('span', 'kp-sticky-atc__title');
    var choice = make('span', 'kp-sticky-atc__choice');
    var price = make('span', 'kp-sticky-atc__price');
    info.appendChild(title);
    info.appendChild(choice);
    info.appendChild(price);

    // Built like the theme's own Add to Cart button (words, plus the theme's "working" line),
    // so the theme's styles for a busy button apply to it as they do to the main one.
    var button = make('button', 'kp-sticky-atc__button btn btn--medium btn--secondary');
    button.type = 'button';
    button.tabIndex = -1;
    var buttonLabel = make('span', 'kp-sticky-atc__button-text');
    var loadingWrap = make('div', 'btn__loading-wrap');
    loadingWrap.appendChild(make('div', 'btn__loading-bar'));
    button.appendChild(buttonLabel);
    button.appendChild(loadingWrap);

    bar.appendChild(photo);
    bar.appendChild(info);
    bar.appendChild(button);
    document.body.appendChild(bar);

    // The product's plain name (the page heading has the model and case type added to it).
    var nameSource = document.querySelector('meta[property="og:title"]');
    var heading = product.querySelector('h1');
    var productName = (nameSource && nameSource.content) || (heading ? heading.textContent : '') || '';
    productName = productName.replace(/\s+/g, ' ').trim();

    var shown = false;

    // The gallery puts the chosen variant's photo first.
    function currentPhoto() {
      var images = product.querySelectorAll('.product__media-item:not(.hidden) img');
      for (var i = 0; i < images.length; i++) {
        if (images[i].offsetParent !== null) return images[i];
      }
      return images[0] || null;
    }

    // The bar's button copies the main button: its words, whether it can be pressed, the
    // "working" line while the item is being added, and the tick once it has been.
    function copyButtonState() {
      var real = mainButton();
      if (!real) return;
      var label = real.querySelector('[data-add-to-cart-text]') || real;
      buttonLabel.textContent = label.textContent.replace(/\s+/g, ' ').trim();
      button.disabled = real.disabled;
      button.classList.toggle('loading', real.classList.contains('loading'));
      button.classList.toggle('kp-added', real.classList.contains('kp-added'));
    }

    // The name on the first line; the choices (model, case type, size...) under it, where a
    // long product name cannot push the model out of view.
    function refresh() {
      var choices = [];
      var selects = product.querySelectorAll('select[name^="options["]');
      for (var i = 0; i < selects.length; i++) {
        if (selects[i].value) choices.push(selects[i].value);
      }
      title.textContent = productName;
      choice.textContent = choices.join(' / ');

      var priceSource = product.querySelector('.product__price [data-price]') || product.querySelector('.product__price');
      price.textContent = priceSource ? priceSource.textContent.replace(/\s+/g, ' ').trim() : '';

      var source = currentPhoto();
      var address = source ? source.currentSrc || source.getAttribute('src') || '' : '';
      if (address && photo.getAttribute('src') !== address) photo.setAttribute('src', address);
      photo.hidden = !address;

      copyButtonState();
    }

    // Previewing an unpublished theme: sit exactly on top of Shopify's preview bar.
    function place() {
      var previewBar = document.getElementById('PBarNextFrameWrapper');
      var height = previewBar ? Math.round(previewBar.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--kp-preview-bar', height + 'px');
    }

    button.addEventListener('click', function () {
      // The main button does the real work, so everything that normally happens still does.
      var real = mainButton();
      if (!real || button.classList.contains('loading')) return;
      pressedButton = real;
      pressedAt = Date.now();
      real.click();
      // Show the main button's reaction straight away, and again shortly after in case it
      // decided not to add anything.
      copyButtonState();
      setTimeout(copyButtonState, 400);
    });

    function setVisible(on) {
      shown = on;
      if (on) {
        place();
        refresh();
      }
      bar.classList.toggle('is-visible', on);
      bar.setAttribute('aria-hidden', on ? 'false' : 'true');
      button.tabIndex = on ? 0 : -1;
      document.documentElement.classList.toggle('kp-sticky-atc-on', on);
    }

    // Show once the main button has been scrolled past (it is above the screen), not before:
    // the shopper should have seen the model picker first.
    function check() {
      var real = mainButton();
      var past = !!real && real.getBoundingClientRect().bottom < 0;
      if (past !== shown) setVisible(past);
    }

    // Fill the bar in and put it in place ahead of time, so the first appearance is a clean
    // float up rather than a jump.
    place();
    refresh();
    check();

    var waiting = false;
    window.addEventListener(
      'scroll',
      function () {
        if (waiting) return;
        waiting = true;
        requestAnimationFrame(function () {
          waiting = false;
          check();
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      check();
      if (shown) place();
    });

    if (window.MutationObserver) {
      // A model or case type was chosen, a price changed, the button was replaced...
      var again = null;
      new MutationObserver(function () {
        clearTimeout(again);
        again = setTimeout(function () {
          check();
          if (shown) refresh();
        }, 60);
      }).observe(product, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });

      // The main button became busy, finished, or got its tick: copy that at once.
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var target = records[i].target;
          if (target && target.nodeType === 1 && target.hasAttribute('data-add-to-cart')) {
            copyButtonState();
            return;
          }
        }
      }).observe(product, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  })();
})();
